import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, FileText, ChevronLeft, Loader2, Calendar, Image } from 'lucide-react';
import { PremiumGate } from '@/components/PremiumLock';

function ChannelDetail() {
  const id = useContentId();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const channelRes = await api.get('/channels/' + id);
        setChannel(channelRes.data);

        const postsRes = await api.get('/channels/' + id + '/posts');
        setPosts(postsRes.data);

        if (user) {
          const followRes = await api.get('/channels/' + id + '/is-following');
          setIsFollowing(followRes.data.is_following);
        }
      } catch (err) {
        toast.error('Erro ao carregar canal');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, user]);

  async function handleFollow() {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (isFollowing) {
        await api.delete('/channels/' + id + '/follow');
        setIsFollowing(false);
        toast.success('Deixou de seguir o canal');
      } else {
        await api.post('/channels/' + id + '/follow');
        setIsFollowing(true);
        toast.success('Está a seguir o canal!');
      }
    } catch (err) {
      toast.error('Erro ao atualizar');
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Canal não encontrado</p>
      </div>
    );
  }

  const postCards = [];
  for (let i = 0; i < posts.length; i++) {
    const post = posts[i];
    const date = new Date(post.published_at);
    const dateStr = date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    postCards.push(
      <article key={post.id} className="bg-surface rounded-xl border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img 
              src={channel.avatar_url || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=200'} 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-medium text-foreground">{channel.name}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {dateStr} às {timeStr}
            </p>
          </div>
        </div>

        <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{post.content}</p>

        {post.image_url && (
          <div className="mt-4 rounded-lg overflow-hidden">
            <img src={post.image_url} alt="" className="w-full max-h-96 object-cover" />
          </div>
        )}
      </article>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <SEOHead title={channel.name} description={channel.description} image={channel.cover_url || channel.avatar_url} type="website" />

      {/* Header/Cover */}
      <div className="relative h-48 md:h-64">
        {channel.cover_url ? (
          <img src={channel.cover_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-ruby/30 to-surface" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />

        <Button
          variant="ghost"
          onClick={function() { navigate('/channels'); }}
          className="absolute top-20 left-6 text-foreground hover:bg-white/10 z-10"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Voltar
        </Button>
      </div>

      <main className="relative z-10 -mt-20 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Channel Info */}
          <div className="flex items-end gap-4 mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-background shadow-xl">
              <img 
                src={channel.avatar_url || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=200'} 
                alt={channel.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 pb-2">
              <h1 className="font-brand text-2xl font-bold text-foreground">{channel.name}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {channel.follower_count} seguidores
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {channel.post_count} posts
                </span>
              </div>
            </div>
            <Button
              onClick={handleFollow}
              data-testid="follow-channel-btn"
              className={'rounded-full px-6 ' + (channel.is_locked ? 'bg-gradient-to-r from-gold to-gold-light text-black hover:from-gold-light hover:to-gold' : (isFollowing ? 'bg-surface-light border border-ruby text-ruby hover:bg-ruby/10' : 'bg-ruby hover:bg-ruby-light text-white'))}
            >
              {channel.is_locked ? 'Assinar Premium' : (isFollowing ? 'Seguindo' : 'Seguir')}
            </Button>
          </div>

          {channel.description && (
            <p className="text-muted-foreground mb-8">{channel.description}</p>
          )}

          {/* Posts */}
          <div className="space-y-6">
            {channel.is_locked ? (
              <PremiumGate
                title="Canal Premium"
                message="Este canal é exclusivo para assinantes Premium. Faz já o upgrade para seguir todos os canais e receber as novidades no teu feed."
              />
            ) : posts.length > 0 ? postCards : (
              <div className="text-center py-12 bg-surface rounded-xl border border-border">
                <FileText className="w-12 h-12 text-muted mx-auto mb-3" />
                <p className="text-muted-foreground">Este canal ainda não tem publicações.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ChannelDetail;
